import { useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, PlusCircle, PackageSearch, History, User, Truck, Wallet,
  Users, Building2, TriangleAlert, Receipt, Settings, ClipboardList, LogOut, Menu, Hourglass, CircleDollarSign, HandCoins, ShieldCheck, FileCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { roleHome } from "@/lib/format";
import { Logo } from "@/components/Logo";
import { NotificationsBell } from "@/components/NotificationsBell";
import { BusinessOnboardingSync } from "@/components/BusinessOnboardingSync";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = {
  BUSINESS: [
    { to: "/business", icon: LayoutDashboard, label: "Panel", end: true },
    { to: "/business/new", icon: PlusCircle, label: "Solicitar envío" },
    { to: "/business/services", icon: PackageSearch, label: "Servicios activos" },
    { to: "/business/account", icon: Wallet, label: "Cuenta y pagos" },
    { to: "/business/documents", icon: FileCheck, label: "Documentos y verificación" },
    { to: "/business/history", icon: History, label: "Historial" },
    { to: "/business/profile", icon: User, label: "Perfil" },
  ],
  DRIVER: [
    { to: "/driver", icon: LayoutDashboard, label: "Panel", end: true },
    { to: "/driver/history", icon: History, label: "Historial" },
    { to: "/driver/earnings", icon: Wallet, label: "Ganancias" },
    { to: "/driver/vehicle", icon: Truck, label: "Vehículo" },
    { to: "/driver/documents", icon: FileCheck, label: "Mis documentos" },
    { to: "/driver/profile", icon: User, label: "Perfil" },
  ],
  ADMIN: [
    { to: "/admin", icon: LayoutDashboard, label: "Panel", end: true },
    { to: "/admin/services", icon: ClipboardList, label: "Servicios" },
    { to: "/admin/stuck", icon: Hourglass, label: "Atascados" },
    { to: "/admin/businesses", icon: Building2, label: "Negocios" },
    { to: "/admin/drivers", icon: Users, label: "Conductores" },
    { to: "/admin/verification", icon: ShieldCheck, label: "Verificación", end: true },
    { to: "/admin/vehicles", icon: Truck, label: "Vehículos" },
    { to: "/admin/incidents", icon: TriangleAlert, label: "Incidencias" },
    { to: "/admin/finance", icon: CircleDollarSign, label: "Finanzas" },
    { to: "/admin/driver-payouts", icon: HandCoins, label: "Pagos a conductores" },
    { to: "/admin/settings", icon: Settings, label: "Configuración" },
  ],
};

const ROLE_LABEL = { BUSINESS: "Negocio", DRIVER: "Conductor", ADMIN: "Administrador" };

const NavItems = ({ items, onNavigate }) => (
  <nav className="flex flex-col gap-1">
    {items.map((it) => {
      const Icon = it.icon;
      return (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onNavigate}
          data-testid={`nav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-all ${
              isActive
                ? "bg-[#FF6A00] text-black shadow-md shadow-[#FF6A00]/20"
                : "text-gray-400 hover:bg-[#18181B] hover:text-white"
            }`
          }
        >
          <Icon size={18} />
          {it.label}
        </NavLink>
      );
    })}
  </nav>
);

export default function RoleLayout({ role }) {
  const { session, profile, loading, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (loading)
    return (
      <div className="grid min-h-screen place-items-center bg-[#0B0B0B]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#27272A] border-t-[#FF6A00]" />
      </div>
    );
  if (!session) return <Navigate to="/auth" replace />;
  if (!profile)
    return (
      <div className="grid min-h-screen place-items-center bg-[#0B0B0B]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#27272A] border-t-[#FF6A00]" />
      </div>
    );
  if (profile.role !== role) return <Navigate to={roleHome(profile.role)} replace />;

  const items = NAV[role];
  const doSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const SidebarBody = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      <div className="px-2 py-5">
        <Logo />
      </div>
      <div className="mb-4 rounded-xl border border-[#27272A] bg-[#18181B] px-3 py-2.5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6A00]">
          {ROLE_LABEL[role]}
        </div>
        <div className="truncate text-sm font-semibold text-white">
          {profile.business_name || profile.full_name || profile.id?.slice(0, 8)}
        </div>
        {role === "DRIVER" && (
          <div className="mt-1 text-[11px] text-gray-400">
            {profile.driver_approval === "APPROVED"
              ? "Cuenta aprobada"
              : profile.driver_approval === "SUSPENDED"
              ? "Cuenta suspendida"
              : "Pendiente de aprobación"}
          </div>
        )}
      </div>
      <div className="grow overflow-auto">
        <NavItems items={items} onNavigate={onNavigate} />
      </div>
      <button
        onClick={doSignOut}
        data-testid="logout-button"
        className="mt-3 flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-gray-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
      >
        <LogOut size={18} /> Cerrar sesión
      </button>
    </div>
  );

  const isApp = role !== "ADMIN"; // BUSINESS/DRIVER use an app shell (bottom nav on mobile)

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      {/* Desktop sidebar (all roles) */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-[#18181B] bg-[#0d0d0f] px-3 lg:flex">
        <SidebarBody />
      </aside>

      {/* Compact app top bar */}
      <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-[#18181B] bg-[#0b0b0b]/95 px-4 py-3 backdrop-blur lg:pl-[17rem]">
        <div className="flex items-center gap-3">
          {/* Admin keeps the drawer on mobile (many sections) */}
          {!isApp && (
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  className="grid h-10 w-10 place-items-center rounded-xl border border-[#27272A] bg-[#18181B] text-gray-300 lg:hidden"
                  data-testid="mobile-menu-button"
                >
                  <Menu size={18} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 border-[#18181B] bg-[#0d0d0f] px-3 text-white">
                <SidebarBody onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
          )}
          <div className="lg:hidden">
            <Logo withText={!isApp} size={30} />
          </div>
          {isApp && (
            <div className="leading-tight lg:hidden">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6A00]">{ROLE_LABEL[role]}</div>
              <div className="max-w-[42vw] truncate text-sm font-bold text-white">
                {profile.business_name || profile.full_name || "RAPIFAST"}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell />
        </div>
      </header>

      <main className={`px-4 py-5 sm:px-6 lg:pl-[17rem] lg:pr-8 lg:py-6 ${isApp ? "pb-28 lg:pb-6" : ""}`}>
        <div className="mx-auto max-w-6xl tf-fade-up">
          {role === "BUSINESS" && <BusinessOnboardingSync />}
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom navigation (BUSINESS / DRIVER) */}
      {isApp && (
        <nav
          className="fixed inset-x-0 bottom-0 z-40 border-t border-[#1e1e21] bg-[#0b0b0b]/95 backdrop-blur lg:hidden"
          data-testid="mobile-bottom-nav"
        >
          <div className="mx-auto flex max-w-md items-stretch justify-around px-1 pb-[env(safe-area-inset-bottom)]">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={`bottomnav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
                  className={({ isActive }) =>
                    `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-bold transition-colors ${
                      isActive ? "text-[#FF6A00]" : "text-gray-500"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`grid h-9 w-9 place-items-center rounded-xl transition-colors ${
                          isActive ? "bg-[#FF6A00]/15" : ""
                        }`}
                      >
                        <Icon size={20} />
                      </span>
                      <span className="max-w-[64px] truncate">{it.label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
